import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DiagnosticsAnalyticsTab from './DiagnosticsAnalyticsTab';
import AudiologistPerformanceTab from './AudiologistPerformanceTab';
import LandscapePrompt from '../../components/LandscapePrompt';

import useEscapeKey from '../../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;


// Reusable helper — compute the number of days between the start/end
// date-inputs so a single header range flows through to every card
// (revenue / audiologists / inventory / funnel / retention / service-revenue
// / diagnosis / referrals). R-5 (2026-08-29).
function computeDaysFromRange(start, end) {
  if (!start) return 90;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const d = Math.max(1, Math.round((e - s) / 86400000));
  return Math.min(d, 3650);
}


export default function OwnerAnalyticsPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('core'); // 'core' | 'diagnostics'
  const [rev, setRev] = useState(null);
  const [aud, setAud] = useState(null);
  const [inv, setInv] = useState(null);
  const [fun, setFun] = useState(null);
  const [ret, setRet] = useState(null);
  const [srev, setSrev] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);   // { title, params }
  const [drillRows, setDrillRows] = useState(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  // R-4 · branch filter, shared between Core Business + Diagnostics tabs.
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  // Active line-level filters (fed by drill-down clicks) — carried into
  // the CSV Sales export so a filtered view matches the downloaded file.
  const [activeBrand, setActiveBrand] = useState('');
  const [activeUserId, setActiveUserId] = useState('');
  const [activeBelowFloor, setActiveBelowFloor] = useState(null);

  useEffect(() => { (async () => {
    try { setMe((await axios.get(`${API}/auth/me`)).data?.user || null); } catch {/*noop*/}
    // Branches — single fetch. Soft dependency, page loads either way.
    try {
      const r = await axios.get(`${API}/branches`);
      setBranches(r.data || []);
    } catch {/* single-branch tenants or partial permission */}
  })(); }, []);

  const canRead = useMemo(() =>
    !!me && ['clinic_owner','super_admin','accounts'].includes(me.role), [me]);
  // Audiologist Performance is a stricter surface — Admin (super_admin) and
  // Clinic-Owner only (accounts is excluded here per the spec).
  const canReadAudPerf = useMemo(() =>
    !!me && ['clinic_owner','super_admin'].includes(me.role), [me]);

  // Days derived from start/end — every endpoint reads this so all
  // cards react to the header date-range change (R-5).
  const daysFromRange = useMemo(() => computeDaysFromRange(start, end), [start, end]);
  const monthsFromRange = useMemo(
    () => Math.max(1, Math.min(36, Math.round(daysFromRange / 30))),
    [daysFromRange],
  );

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const branchParam = branchId ? { branch_id: branchId } : {};
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        axios.get(`${API}/ha/analytics/revenue`,         { params: { months: monthsFromRange, ...branchParam } }),
        axios.get(`${API}/ha/analytics/audiologists`,    { params: { days: daysFromRange, ...branchParam } }),
        axios.get(`${API}/ha/analytics/inventory`,       { params: { days: daysFromRange, ...branchParam } }),
        axios.get(`${API}/ha/analytics/funnel`,          { params: { days: daysFromRange, ...branchParam } }),
        axios.get(`${API}/ha/analytics/retention`,       { params: { days: daysFromRange, ...branchParam } }),
        axios.get(`${API}/ha/analytics/service-revenue`, { params: { days: daysFromRange, ...branchParam } }),
      ]);
      setRev(r1.data); setAud(r2.data); setInv(r3.data); setFun(r4.data); setRet(r5.data); setSrev(r6.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [start, end, branchId, daysFromRange, monthsFromRange]);

  useEffect(() => { if (canRead) load(); else setLoading(false); }, [canRead, load]);

  const openDrill = async (title, params) => {
    setDrill({ title });
    setDrillRows(null);
    // Track any brand / user / below_floor filter driven by the drill click
    // so a follow-up CSV export mirrors it (R-7).
    setActiveBrand(params?.brand || '');
    setActiveUserId(params?.user_id || '');
    setActiveBelowFloor(typeof params?.below_floor === 'boolean' ? params.below_floor : null);
    try {
      const merged = { ...params };
      if (branchId && !merged.branch_id) merged.branch_id = branchId;
      const r = await axios.get(`${API}/ha/analytics/sales-drill`, { params: merged });
      setDrillRows(r.data.rows);
    } catch {
      setDrillRows([]);
    }
  };

  const closeDrill = () => {
    setDrill(null);
    setDrillRows(null);
    setActiveBrand('');
    setActiveUserId('');
    setActiveBelowFloor(null);
  };

  const exportCsv = async (kind) => {
    try {
      const params = {};
      // Header date-range flows through to every kind.
      if (kind === 'sales') {
        if (start) params.start = start;
        if (end) params.end = end;
        // R-7 — apply the same active filters as the on-screen dashboard.
        if (branchId) params.branch_id = branchId;
        if (activeBrand) params.brand = activeBrand;
        if (activeUserId) params.user_id = activeUserId;
        if (activeBelowFloor !== null) params.below_floor = activeBelowFloor;
      } else if (kind === 'revenue') {
        params.months = monthsFromRange;
        if (branchId) params.branch_id = branchId;
      } else if (kind === 'inventory') {
        if (branchId) params.branch_id = branchId;
      }
      const r = await axios.get(`${API}/ha/analytics/export/${kind}.csv`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ha_${kind}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e?.response?.status || 'error'));
    }
  };

  if (!me) return <div className="p-6 text-slate-400 italic text-sm">Loading session…</div>;
  if (!canRead) return (
    <div className="p-6" data-testid="ha-analytics-denied">
      <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-md p-4 text-sm">
        Analytics is reserved for Clinic Owner, Super Admin & Accounts roles. Your role: <b>{me.role}</b>.
      </div>
    </div>
  );

  const activeBranch = branches.find(b => b.branch_id === branchId);
  const branchLabel = activeBranch ? (activeBranch.name || activeBranch.branch_id) : 'All branches';

  return (
    <div className="p-5 space-y-5" data-testid="ha-analytics-page">
      <LandscapePrompt
        featureKey="analytics_dashboard"
        message="Rotate to landscape for wider charts and side-by-side KPI cards."
        testid="analytics-landscape"
      />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Reports &amp; Analytics</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {tab === 'core'
              ? `Hearing-aid sales, service & repair, brand split, team performance. · ${branchLabel}`
              : `Diagnostic tests, recommendations, age/gender split, and referral pathways for marketing planning. · ${branchLabel}`}
          </p>
        </div>
      </div>

      {/* Top-level tab bar — clean two-pill switcher */}
      <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1" data-testid="analytics-tabs">
        <button
          type="button"
          onClick={() => setTab('core')}
          data-testid="tab-core-business"
          className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            tab === 'core'
              ? 'bg-white text-indigo-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Core Business Analytics
        </button>
        <button
          type="button"
          onClick={() => setTab('diagnostics')}
          data-testid="tab-diagnostics"
          className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            tab === 'diagnostics'
              ? 'bg-white text-indigo-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Diagnostics Analytics
        </button>
        {canReadAudPerf && (
          <button
            type="button"
            onClick={() => setTab('audiologist')}
            data-testid="tab-audiologist-performance"
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              tab === 'audiologist'
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Audiologist Performance
          </button>
        )}
      </div>

      {/* Header controls — branch + date range + refresh — visible on BOTH tabs */}
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          <label className="inline-flex items-center gap-1 text-slate-600">
            <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">Branch</span>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              data-testid="ha-analytics-branch-filter"
              className="border border-slate-300 rounded px-2 py-1 text-xs font-semibold bg-white"
            >
              <option value="">All branches</option>
              {branches.map(b => (
                <option key={b.branch_id} value={b.branch_id}>{b.name || b.branch_id}</option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-1 text-slate-600">
            From <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ha-analytics-start" className="border border-slate-300 rounded px-1 py-0.5 text-xs" />
          </label>
          <label className="inline-flex items-center gap-1 text-slate-600">
            To <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ha-analytics-end" className="border border-slate-300 rounded px-1 py-0.5 text-xs" />
          </label>
          {(start || end) && <button onClick={() => { setStart(''); setEnd(''); }} className="text-[10px] text-slate-500 hover:underline" data-testid="ha-analytics-range-clear">Clear</button>}
          {tab === 'core' && (
            <div className="ml-2 flex items-center gap-1 border-l border-slate-200 pl-2">
              <button onClick={() => exportCsv('sales')} data-testid="ha-analytics-csv-sales" className="px-2 py-1 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 rounded" title={activeBrand || activeUserId || activeBelowFloor !== null || branchId ? 'CSV mirrors currently applied filters' : 'CSV of entire window'}>
                CSV · Sales{(activeBrand || activeUserId || activeBelowFloor !== null || branchId) ? ' *' : ''}
              </button>
              <button onClick={() => exportCsv('revenue')} data-testid="ha-analytics-csv-revenue" className="px-2 py-1 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 rounded">CSV · Revenue</button>
              <button onClick={() => exportCsv('inventory')} data-testid="ha-analytics-csv-inventory" className="px-2 py-1 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 rounded">CSV · Inventory</button>
            </div>
          )}
          <button onClick={load} disabled={loading} className="text-[11px] text-indigo-600 font-semibold hover:underline" data-testid="ha-analytics-refresh">
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Active-filter chip strip — visible on Core tab whenever any filter is set */}
      {tab === 'core' && (activeBrand || activeUserId || activeBelowFloor !== null || branchId) && (
        <div className="flex items-center gap-2 text-[10px]" data-testid="ha-analytics-active-filters">
          <span className="text-slate-500 font-semibold uppercase tracking-wider">Active filters:</span>
          {branchId && <FilterChip label={`Branch: ${branchLabel}`} onClear={() => setBranchId('')} />}
          {activeBrand && <FilterChip label={`Brand: ${activeBrand}`} onClear={() => { setActiveBrand(''); load(); }} />}
          {activeUserId && <FilterChip label={`User: ${activeUserId}`} onClear={() => { setActiveUserId(''); load(); }} />}
          {activeBelowFloor !== null && <FilterChip label={`Below-floor: ${activeBelowFloor ? 'yes' : 'no'}`} onClear={() => { setActiveBelowFloor(null); load(); }} />}
        </div>
      )}

      {tab === 'diagnostics' ? (
        <DiagnosticsAnalyticsTab
          branchId={branchId}
          days={daysFromRange}
          onNavigate={navigate}
        />
      ) : tab === 'audiologist' && canReadAudPerf ? (
        <AudiologistPerformanceTab branchId={branchId} />
      ) : (
      <>
      {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded" data-testid="ha-analytics-err">{err}</div>}

      {/* ========== TOP-LINE KPIs — every card drills to a filtered detail view ========== */}
      {rev && (
        <div className="grid grid-cols-4 gap-3" data-testid="ha-analytics-revenue-kpis">
          <Kpi
            label="Total Revenue (12 mo)"
            value={fmtINR(rev.total_revenue)}
            accent="indigo"
            testid="ha-analytics-kpi-revenue"
            onClick={() => openDrill('All paid sales · this window', { start, end, days: daysFromRange })}
          />
          <Kpi
            label="Sales (12 mo)"
            value={rev.total_sales_count}
            accent="emerald"
            testid="ha-analytics-kpi-sales"
            onClick={() => openDrill('All sales · this window', { start, end, days: daysFromRange })}
          />
          <Kpi
            label="Avg Ticket"
            value={fmtINR(rev.avg_ticket)}
            accent="blue"
            testid="ha-analytics-kpi-ticket"
            onClick={() => openDrill('All sales · avg ticket drill', { start, end, days: daysFromRange })}
          />
          <Kpi
            label="Active Subscriptions"
            value={ret?.active_subscriptions ?? 0}
            accent="teal"
            testid="ha-analytics-kpi-subs"
            onClick={() => navigate('/ha/subscriptions')}
          />
        </div>
      )}

      {/* ========== MONTHLY REVENUE ========== */}
      <Card title="Monthly Revenue" subtitle="Last 12 months · paid + invoiced + reserved (cancelled excluded) · click bar to drill" testid="ha-analytics-revenue-card">
        {!rev ? <Skel /> : rev.monthly.length === 0 ? <Empty label="No sales in window." /> : (
          <RevenueChart data={rev.monthly} onDrill={(m) => openDrill(`Sales in ${m.month}`, { start: `${m.month}-01`, end: nextMonth(m.month) })} />
        )}
      </Card>

      <div className="grid grid-cols-2 gap-5">
        {/* ========== BRAND SPLIT ========== */}
        <Card title="Brand Split" subtitle="Revenue by brand · last 12 months">
          {!rev ? <Skel /> : rev.brand_split.length === 0 ? <Empty /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-1">Brand</th><th className="text-right">Units</th><th className="text-right">Revenue</th><th className="text-right w-24">Share</th></tr>
              </thead>
              <tbody>
                {(() => {
                  const total = rev.brand_split.reduce((s, b) => s + b.revenue, 0) || 1;
                  return rev.brand_split.map(b => (
                    <tr key={b.brand} onClick={() => openDrill(`Sales · ${b.brand}`, { brand: b.brand, days: daysFromRange })} className="border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer" data-testid={`ha-analytics-brand-${b.brand}`}>
                      <td className="py-1 font-semibold">{b.brand}</td>
                      <td className="text-right tabular-nums">{b.units}</td>
                      <td className="text-right tabular-nums font-mono">{fmtINR(b.revenue)}</td>
                      <td className="text-right">
                        <div className="flex items-center gap-1">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded"><div className="h-1.5 bg-indigo-500 rounded" style={{ width: `${(100 * b.revenue / total).toFixed(0)}%` }} /></div>
                          <span className="text-[10px] text-slate-500 w-8 text-right">{fmtPct(100 * b.revenue / total)}</span>
                        </div>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          )}
        </Card>

        {/* ========== AUDIOLOGIST PERFORMANCE ========== */}
        <Card title="Team Performance" subtitle={`Last ${daysFromRange} days · sales · below-floor · WA response rate`} testid="ha-analytics-team-card">
          {!aud ? <Skel /> : (aud.rows?.length ?? 0) === 0 && (aud.unattributed_sales?.sales_count ?? 0) === 0 ? <Empty /> : (
            <>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left py-1">Name</th><th className="text-right">Sales</th><th className="text-right">Revenue</th><th className="text-right">Below-Floor</th><th className="text-right">WA Response</th></tr>
                </thead>
                <tbody>
                  {(aud.rows || []).map(r => (
                    <tr key={r.user_id} onClick={() => openDrill(`Sales · ${r.name}`, { user_id: r.user_id, days: daysFromRange })} className="border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer" data-testid={`ha-analytics-aud-${r.user_id}`}>
                      <td className="py-1">
                        <div className="font-semibold">{r.name}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">{r.role}</div>
                      </td>
                      <td className="text-right tabular-nums">{r.sales_count}</td>
                      <td className="text-right tabular-nums font-mono">{fmtINR(r.revenue)}</td>
                      <td className="text-right">
                        <span
                          onClick={(e) => { e.stopPropagation(); if (r.below_floor_count > 0) openDrill(`Below-floor sales · ${r.name}`, { user_id: r.user_id, below_floor: true, days: daysFromRange }); }}
                          className={`text-[10px] font-bold px-1 py-0.5 rounded ${r.below_floor_count > 0 ? 'cursor-pointer underline decoration-dotted' : ''} ${r.below_floor_pct > 25 ? 'bg-rose-100 text-rose-800' : r.below_floor_pct > 10 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}
                          data-testid={`ha-analytics-aud-below-floor-${r.user_id}`}
                        >
                          {fmtPct(r.below_floor_pct)}
                        </span>
                      </td>
                      <td className="text-right">
                        <ResponseRateBar sends={r.wa_sends} done={r.wa_done} pct={r.response_rate_pct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {aud.unattributed_sales?.sales_count > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2 flex items-center justify-between bg-slate-50 -mx-4 px-4 py-2 rounded-b" data-testid="ha-analytics-unattributed">
                  <div>
                    <div className="text-[11px] font-bold text-slate-700">Unattributed sales</div>
                    <div className="text-[10px] text-slate-500">Legacy CSV imports — no audiologist stamp on row</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] font-mono font-bold text-slate-700">{fmtINR(aud.unattributed_sales.revenue)}</div>
                    <div className="text-[10px] text-slate-500">{aud.unattributed_sales.sales_count} sale{aud.unattributed_sales.sales_count === 1 ? '' : 's'}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* ========== FUNNEL — click any bar to drill ========== */}
      <Card title="Commercial Funnel" subtitle={`Last ${daysFromRange} days · consultation → quote → trial → sale → paid`} testid="ha-analytics-funnel-card">
        {!fun ? <Skel /> : (
          <FunnelView
            data={fun}
            onDrill={(stage) => openDrill(
              `Funnel drill · ${stage.replace(/_/g, ' ')}`,
              { stage, days: daysFromRange },
            )}
          />
        )}
      </Card>

      <div className="grid grid-cols-2 gap-5">
        {/* ========== INVENTORY HEALTH — click SKU row to drill ========== */}
        <Card title="Inventory Health" subtitle={`Aging > ${inv?.aging_days || 90}d · Dead > ${inv?.dead_days || 180}d`} testid="ha-analytics-inventory-card">
          {!inv ? <Skel /> : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <MiniKpi label="In Stock" v={inv.totals.in_stock_total} onClick={() => navigate('/ha/inventory')} testid="ha-analytics-mini-stock" />
                <MiniKpi label="Aging" v={inv.totals.aging_units} color={inv.totals.aging_units > 0 ? 'text-amber-700' : ''} onClick={() => navigate('/ha/inventory')} testid="ha-analytics-mini-aging" />
                <MiniKpi label="Dead" v={inv.totals.dead_units} color={inv.totals.dead_units > 0 ? 'text-rose-700' : ''} onClick={() => navigate('/ha/inventory')} testid="ha-analytics-mini-dead" />
              </div>
              {inv.aging_by_product.length === 0 ? <div className="text-[11px] italic text-slate-400">No aging stock — inventory is moving.</div> : (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left py-1">Product</th><th className="text-right">Stock</th><th className="text-right">Aging</th><th className="text-right">Dead</th></tr>
                  </thead>
                  <tbody>
                    {inv.aging_by_product.slice(0, 8).map(p => (
                      <tr key={p.product_id} onClick={() => navigate(`/ha/inventory?product_id=${encodeURIComponent(p.product_id)}`)} className="border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer" data-testid={`ha-analytics-aging-${p.product_id}`}>
                        <td className="py-1"><span className="font-semibold">{p.brand}</span> <span className="text-slate-500">· {p.model}</span></td>
                        <td className="text-right tabular-nums">{p.in_stock}</td>
                        <td className="text-right tabular-nums text-amber-700 font-bold">{p.aging}</td>
                        <td className="text-right tabular-nums text-rose-700 font-bold">{p.dead}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Card>

        {/* ========== RETENTION — each row is clickable ========== */}
        <Card title="Retention Health" subtitle="CRM + subscription loyalty + upgrade pipeline" testid="ha-analytics-retention-card">
          {!ret ? <Skel /> : (
            <div className="space-y-2">
              <BigMetric label="Missed Follow-ups" value={ret.missed_followups} tone={ret.missed_followups > 0 ? 'rose' : 'slate'} hint="Pending queue overdue — action required" testid="ha-analytics-missed" onClick={() => navigate('/ha/followups?status=pending')} />
              <BigMetric label="Active Subscriptions" value={ret.active_subscriptions} tone="emerald" hint="Patients with a recurring consumable cadence" testid="ha-analytics-active-subs" onClick={() => navigate('/ha/subscriptions')} />
              <BigMetric label="Repeat Loyalty Patients" value={ret.loyal_repeat_patients} tone="indigo" hint="≥2 consumable deliveries on record" testid="ha-analytics-loyal" onClick={() => navigate('/ha/subscriptions?filter=loyal')} />
              <BigMetric label="Upgrade Pipeline" value={ret.upgrade_pipeline_size} tone="amber" hint="Paid HA sales older than 3 years" testid="ha-analytics-upgrade" onClick={() => navigate('/ha/upgrades')} />
              <div className="text-[10px] text-slate-400 italic pt-1">Dismissed: {ret.dismissed_followups} ({fmtPct(ret.dismissed_pct)} of all follow-ups)</div>
            </div>
          )}
        </Card>
      </div>

      {/* ========== WA RESPONSE RATE ========== */}
      <Card title="Response Rate per Audiologist" subtitle={`Follow-ups sent → marked done · last ${daysFromRange} days · patient-reply proxy`} testid="ha-analytics-response-card">
        {!aud ? <Skel /> : (() => {
          const senders = (aud.rows || []).filter(r => r.wa_sends > 0);
          if (senders.length === 0) return <Empty label="No follow-ups sent in window." />;
          senders.sort((a, b) => b.wa_sends - a.wa_sends);
          return (
            <div className="space-y-2">
              {senders.map(r => (
                <div key={r.user_id} className="flex items-center gap-3" data-testid={`ha-analytics-response-${r.user_id}`}>
                  <div className="w-36 truncate">
                    <div className="text-xs font-semibold text-slate-800">{r.name}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wide">{r.role}</div>
                  </div>
                  <div className="flex-1 h-4 bg-slate-100 rounded relative overflow-hidden" title={`${r.wa_done} done of ${r.wa_sends} sent`}>
                    <div className={`absolute inset-y-0 left-0 rounded transition ${r.response_rate_pct >= 50 ? 'bg-emerald-500' : r.response_rate_pct >= 25 ? 'bg-amber-500' : 'bg-rose-500'}`}
                         style={{ width: `${Math.min(100, r.response_rate_pct)}%` }} />
                    <div className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-slate-800">
                      {fmtPct(r.response_rate_pct)}
                    </div>
                  </div>
                  <div className="w-28 text-right text-[10px] text-slate-500">
                    <span className="font-bold text-emerald-700">{r.wa_done}</span> done · <span className="font-bold text-indigo-700">{r.wa_sends}</span> sent
                  </div>
                </div>
              ))}
              <div className="pt-2 mt-2 border-t border-slate-100 text-[10px] text-slate-500 italic">
                Bar colour: <span className="text-emerald-700 font-bold">≥50% green</span> · <span className="text-amber-700 font-bold">25-49% amber</span> · <span className="text-rose-700 font-bold">&lt;25% rose</span>.
              </div>
            </div>
          );
        })()}
      </Card>

      {/* ========== SERVICE REVENUE & WARRANTY BURDEN ========== */}
      {srev && <ServiceRevenueCard data={srev} onDrill={(kind) => navigate(`/repair?kind=${encodeURIComponent(kind)}`)} />}

      {/* Warnings surfaced by the funnel endpoint when the pipeline has out-of-order writes */}
      {fun?.warnings && fun.warnings.length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[11px] p-3" data-testid="ha-analytics-warnings">
          <div className="font-bold uppercase tracking-wider text-[10px] mb-1">Pipeline anomalies</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {fun.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {drill && <DrillModal title={drill.title} rows={drillRows} onClose={closeDrill} onExport={() => exportCsv('sales')} navigate={navigate} />}
      </>
      )}
    </div>
  );
}


function ResponseRateBar({ sends, done, pct }) {
  if (!sends) return <span className="text-[10px] text-slate-400">—</span>;
  const color = pct >= 50 ? 'bg-emerald-500' : pct >= 25 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="inline-flex items-center gap-1.5 min-w-[110px] justify-end" title={`${done} done of ${sends} sent`}>
      <div className="w-14 h-1.5 bg-slate-200 rounded">
        <div className={`h-1.5 rounded ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className={`text-[10px] font-bold tabular-nums ${pct >= 50 ? 'text-emerald-700' : pct >= 25 ? 'text-amber-700' : 'text-rose-700'}`}>{pct.toFixed(0)}%</span>
      <span className="text-[9px] text-slate-400">({done}/{sends})</span>
    </div>
  );
}


function nextMonth(ymStr) {
  const [y, m] = ymStr.split('-').map(Number);
  const d = new Date(y, m, 1);
  return d.toISOString().slice(0, 10);
}


function DrillModal({ title, rows, onClose, onExport, navigate }) {
  useEscapeKey(onClose);
  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ha-analytics-drill-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-800">{title}</div>
            <div className="text-[10px] text-slate-500">{rows ? `${rows.length} row(s)` : 'Loading…'}</div>
          </div>
          <div className="flex items-center gap-2">
            {rows?.length > 0 && (
              <button onClick={onExport} data-testid="ha-analytics-drill-csv" className="px-2 py-1 text-[10px] font-semibold bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded">Download CSV of this view</button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="ha-analytics-drill-close">✕</button>
          </div>
        </div>
        <div className="p-4">
          {!rows ? (
            <div className="h-32 bg-slate-100 rounded animate-pulse" />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-400 italic text-xs">No sales match.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-1">Sale No</th><th className="text-left">Date</th><th className="text-left">Patient</th><th className="text-right">Lines</th><th className="text-right">Total</th><th className="text-left">Status</th></tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.sale_no} onClick={() => { if (s.sale_no && s.sale_no.startsWith?.('SL')) navigate(`/ha/fittings`); }} className="border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer" data-testid={`ha-analytics-drill-row-${s.sale_no}`}>
                    <td className="py-1 font-mono font-bold text-indigo-700">{s.sale_no}</td>
                    <td className="text-slate-500 text-[10px]">{s.created_at?.slice(0, 10)}</td>
                    <td>{s.patient_name || s.patient_id}</td>
                    <td className="text-right tabular-nums">{s.lines?.length || 0}</td>
                    <td className="text-right tabular-nums font-mono font-semibold">{fmt(s.total)}</td>
                    <td><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : s.status === 'cancelled' ? 'bg-rose-100 text-rose-800' : 'bg-slate-200 text-slate-600'}`}>{s.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// ============ SHARED SUB-COMPONENTS ============

const Card = ({ title, subtitle, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={testid}>
    <div className="mb-2">
      <div className="text-sm font-bold text-slate-800">{title}</div>
      {subtitle && <div className="text-[10px] text-slate-500 uppercase tracking-wider">{subtitle}</div>}
    </div>
    {children}
  </div>
);

const Kpi = ({ label, value, accent = 'indigo', testid, onClick }) => {
  const color = {
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
  }[accent];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`text-left border rounded-md px-3 py-2 ${color} ${onClick ? 'hover:shadow-md hover:-translate-y-px transition cursor-pointer' : 'cursor-default'}`}
    >
      <div className="text-[9px] font-semibold uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value ?? '—'}</div>
    </button>
  );
};

const MiniKpi = ({ label, v, color = 'text-slate-800', onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testid}
    className={`w-full border border-slate-200 rounded px-2 py-1.5 text-center ${onClick ? 'hover:bg-indigo-50 hover:border-indigo-300 cursor-pointer transition' : 'cursor-default'}`}
  >
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-lg font-bold tabular-nums ${color}`}>{v}</div>
  </button>
);

const BigMetric = ({ label, value, tone = 'slate', hint, testid, onClick }) => {
  const tones = {
    rose: 'text-rose-700', slate: 'text-slate-700',
    emerald: 'text-emerald-700', indigo: 'text-indigo-700', amber: 'text-amber-700',
  };
  return (
    <div
      onClick={onClick}
      className={`flex items-baseline justify-between py-1.5 border-b border-slate-100 last:border-0 ${onClick ? 'cursor-pointer hover:bg-indigo-50/50 rounded px-2 -mx-2' : ''}`}
      data-testid={testid}
    >
      <div>
        <div className="text-xs font-semibold text-slate-700">{label}</div>
        {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
      </div>
      <div className={`text-xl font-bold tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
};

const Skel = () => <div className="h-24 rounded bg-slate-100 animate-pulse" />;
const Empty = ({ label = 'No data yet.' }) => <div className="text-[11px] italic text-slate-400 py-4 text-center">{label}</div>;

const FilterChip = ({ label, onClear }) => (
  <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5 font-semibold">
    {label}
    <button onClick={onClear} className="text-indigo-400 hover:text-indigo-700 leading-none">×</button>
  </span>
);


// ============ MINI REVENUE CHART (pure CSS bars) ============
function RevenueChart({ data, onDrill }) {
  const max = Math.max(...data.map(m => m.revenue), 1);
  return (
    <div className="flex items-end gap-2 h-32 pt-4" data-testid="ha-analytics-revenue-chart">
      {data.map(m => (
        <button
          key={m.month}
          onClick={() => onDrill && onDrill(m)}
          className="flex-1 flex flex-col items-center cursor-pointer group"
          title={`${m.month}: ₹${m.revenue.toLocaleString('en-IN')} · ${m.sales_count} sales · click to drill`}
          data-testid={`ha-analytics-revenue-bar-${m.month}`}
        >
          <div className="relative w-full flex items-end h-28">
            <div className="w-full bg-indigo-500 group-hover:bg-indigo-700 rounded-t transition"
                 style={{ height: `${(100 * m.revenue / max).toFixed(1)}%` }} />
          </div>
          <div className="text-[9px] text-slate-500 mt-1 font-mono">{m.month.slice(5)}</div>
          <div className="text-[9px] text-slate-700 font-semibold">{fmtINR(m.revenue).replace('₹', '')}</div>
        </button>
      ))}
    </div>
  );
}


// ============ FUNNEL (horizontal bars — each bar is clickable) ============
function FunnelView({ data, onDrill }) {
  const { stages, rates, avg_trial_to_convert_days } = data;
  const steps = [
    ['Consultations', stages.consultations,     null, 'consultations'],
    ['Quotations',    stages.quotations,        rates.quote_per_consult_pct, 'quotations'],
    ['Trials issued', stages.trials_issued,     rates.trial_per_quote_pct, 'trials_issued'],
    ['Converted',     stages.trials_converted,  rates.convert_per_trial_pct, 'trials_converted'],
    ['Sales (total)', stages.sales_total,       null, 'sales_total'],
    ['Sales paid',    stages.sales_paid,        rates.paid_per_sale_pct, 'sales_paid'],
  ];
  const max = Math.max(...steps.map(s => s[1]), 1);
  return (
    <div className="space-y-1" data-testid="ha-analytics-funnel">
      {steps.map(([label, value, rate, stage]) => (
        <button
          key={label}
          type="button"
          onClick={() => onDrill && onDrill(stage)}
          className="w-full flex items-center gap-2 text-xs hover:bg-indigo-50/40 rounded px-1 transition"
          data-testid={`ha-analytics-funnel-${stage}`}
        >
          <div className="w-32 text-slate-700 text-left">{label}</div>
          <div className="flex-1 h-5 bg-slate-100 rounded relative">
            <div className="absolute inset-y-0 left-0 bg-indigo-500 rounded" style={{ width: `${(100 * value / max).toFixed(0)}%` }} />
            <div className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-slate-800 tabular-nums">{value}</div>
          </div>
          <div className="w-16 text-right text-[10px] text-slate-500 font-semibold">{rate != null ? fmtPct(rate) : ''}</div>
        </button>
      ))}
      <div className="pt-2 text-[10px] text-slate-500">
        Avg trial-to-convert: <b className="text-slate-800">{avg_trial_to_convert_days != null ? `${avg_trial_to_convert_days} days` : '—'}</b>
        {' · '}Trials returned: <b className="text-slate-800">{stages.trials_returned}</b>
        {' · '}Trials lost: <b className="text-rose-700">{stages.trials_lost}</b>
      </div>
    </div>
  );
}


// ============ SERVICE REVENUE & WARRANTY BURDEN ============
function ServiceRevenueCard({ data, onDrill }) {
  const totals = data?.totals || { paid_revenue: 0, warranty_tickets: 0, total_tickets: 0 };
  const byKind = data?.by_kind || [];
  const byTech = data?.by_technician || [];
  const windowDays = data?.window_days || 90;
  const warrantyPct = totals.total_tickets > 0
    ? (100 * totals.warranty_tickets / totals.total_tickets)
    : 0;

  return (
    <Card
      title="Service Revenue & Warranty Burden"
      subtitle={`Last ${windowDays} days · resolved + closed tickets · warranty tickets are zero-revenue cost centres`}
      testid="ha-analytics-service-revenue-card"
    >
      <div className="grid grid-cols-4 gap-2 mb-4">
        <MiniKpi label="Paid Revenue" v={fmtINR(totals.paid_revenue)} color="text-emerald-700" />
        <MiniKpi label="Total Tickets" v={totals.total_tickets} />
        <MiniKpi label="Warranty-Covered" v={totals.warranty_tickets} color={totals.warranty_tickets > 0 ? 'text-amber-700' : ''} />
        <MiniKpi label="Warranty Burden" v={fmtPct(warrantyPct)} color={warrantyPct > 30 ? 'text-rose-700' : warrantyPct > 15 ? 'text-amber-700' : 'text-slate-800'} />
      </div>

      {totals.total_tickets === 0 ? (
        <Empty label="No resolved service tickets in window." />
      ) : (
        <div className="grid grid-cols-2 gap-5">
          <div data-testid="ha-analytics-service-by-kind">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">By Ticket Kind</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-1">Kind</th>
                  <th className="text-right">Tickets</th>
                  <th className="text-right">Warranty</th>
                  <th className="text-right">Paid ₹</th>
                </tr>
              </thead>
              <tbody>
                {byKind.map(r => (
                  <tr key={r.kind} onClick={() => onDrill && onDrill(r.kind)} className="border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer" data-testid={`ha-analytics-service-kind-${r.kind}`}>
                    <td className="py-1 font-semibold capitalize">{r.kind.replace(/_/g, ' ')}</td>
                    <td className="text-right tabular-nums">{r.ticket_count}</td>
                    <td className="text-right tabular-nums text-amber-700 font-bold">{r.warranty_tickets}</td>
                    <td className="text-right tabular-nums font-mono font-semibold">{fmtINR(r.paid_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div data-testid="ha-analytics-service-by-tech">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">By Technician</div>
            {byTech.length === 0 ? (
              <div className="text-[11px] italic text-slate-400 py-4">No technician assignments yet.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-1">Technician</th>
                    <th className="text-right">Tickets</th>
                    <th className="text-right">Warranty</th>
                    <th className="text-right">Paid ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {byTech.map(r => (
                    <tr key={r.user_id} className="border-t border-slate-100" data-testid={`ha-analytics-service-tech-${r.user_id}`}>
                      <td className="py-1">
                        <div className="font-semibold">{r.name}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">{r.role}</div>
                      </td>
                      <td className="text-right tabular-nums">{r.ticket_count}</td>
                      <td className="text-right tabular-nums text-amber-700 font-bold">{r.warranty_tickets}</td>
                      <td className="text-right tabular-nums font-mono font-semibold">{fmtINR(r.paid_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <div className="pt-3 mt-3 border-t border-slate-100 text-[10px] text-slate-500 italic">
        Warranty burden &gt;30% often signals a product quality issue or over-promising on warranty at the point of sale. Coach technicians whose warranty share is disproportionately high.
      </div>
    </Card>
  );
}
